/** Booking horizon + seasonal months — mirrors backend BookingAvailability. */

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ALL_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const DEFAULT_HORIZON = 12;
export const GROUP_REGISTRATION_CUTOFF_DAYS = 14;

let cachedHorizon = null;

function channelMode(journey, channel) {
	const key = channel === 'private' ? 'private_availability_mode' : 'group_availability_mode';
	return journey?.[key] || 'all_year';
}

function channelStoredMonths(journey, channel) {
	const key = channel === 'private' ? 'private_available_months_stored' : 'group_available_months_stored';
	if (Array.isArray(journey?.[key])) return journey[key].map(Number).filter((m) => m >= 1 && m <= 12).sort((a, b) => a - b);
	return null;
}

/** Months allowed for booking UI — empty array = closed; all 12 = all year. */
export function normalizeAvailableMonths(journey, channel = 'group') {
	const mode = channelMode(journey, channel);
	if (mode === 'closed') return [];
	const stored = channelStoredMonths(journey, channel);
	if (stored) return stored.length ? stored : [];
	if (mode === 'all_year') return [...ALL_MONTHS];

	const fromJourney = journey?.available_months;
	if (Array.isArray(fromJourney) && fromJourney.length > 0) {
		return fromJourney.map(Number).filter((m) => m >= 1 && m <= 12).sort((a, b) => a - b);
	}
	const fromDefaults = journey?.booking_defaults?.available_months;
	if (Array.isArray(fromDefaults) && fromDefaults.length > 0) {
		return fromDefaults.map(Number).filter((m) => m >= 1 && m <= 12).sort((a, b) => a - b);
	}
	return [...ALL_MONTHS];
}

export function formatMonthRanges(months) {
	if (!months?.length || months.length === 12) return 'All year';
	const groups = [];
	let current = [months[0]];
	for (let i = 1; i < months.length; i += 1) {
		if (months[i] === current[current.length - 1] + 1) {
			current.push(months[i]);
		} else {
			groups.push(current);
			current = [months[i]];
		}
	}
	groups.push(current);
	return groups.map((g) => (g.length === 1 ? MONTH_ABBR[g[0] - 1] : `${MONTH_ABBR[g[0] - 1]}–${MONTH_ABBR[g[g.length - 1] - 1]}`)).join(', ');
}

export function seasonsLabel(journey, channel = null) {
	if (channel === 'group' && journey?.group_available_months_label) return journey.group_available_months_label;
	if (channel === 'private' && journey?.private_available_months_label) return journey.private_available_months_label;
	if (journey?.available_months_label) return journey.available_months_label;
	return formatMonthRanges(normalizeAvailableMonths(journey, channel || 'group'));
}

/** Initial picker state for admin modal. */
export function monthsForPicker(journey, channel = 'group') {
	const mode = channelMode(journey, channel);
	if (mode === 'closed') return [];
	const stored = channelStoredMonths(journey, channel);
	if (stored?.length) return stored;
	return [...ALL_MONTHS];
}

/** Raw stored months for admin pickers (legacy single field). */
export function storedAvailableMonths(journey) {
	if (Array.isArray(journey?.available_months_stored)) {
		return journey.available_months_stored.map(Number).filter((m) => m >= 1 && m <= 12).sort((a, b) => a - b);
	}
	const fromDefaults = journey?.booking_defaults?.available_months;
	if (Array.isArray(fromDefaults)) {
		return fromDefaults.map(Number).filter((m) => m >= 1 && m <= 12).sort((a, b) => a - b);
	}
	return [];
}

export function isAllYearAvailability(journey, channel = null) {
	if (channel === 'group') return channelMode(journey, 'group') === 'all_year';
	if (channel === 'private') return channelMode(journey, 'private') === 'all_year';
	return storedAvailableMonths(journey).length === 0;
}

export function isChannelClosed(journey, channel) {
	return channelMode(journey, channel) === 'closed';
}

export async function fetchBookingHorizon(apiBase = process.env.REACT_APP_BASE_URL) {
	if (cachedHorizon != null) return cachedHorizon;
	try {
		const res = await fetch(`${apiBase}/api/booking_availability/config`);
		if (res.ok) {
			const data = await res.json();
			const n = Number(data.max_booking_months_ahead);
			cachedHorizon = n > 0 ? n : DEFAULT_HORIZON;
			return cachedHorizon;
		}
	} catch {
		// fall through
	}
	cachedHorizon = DEFAULT_HORIZON;
	return cachedHorizon;
}

/** Same rules as GroupDepartureReservationService#month_bookable? */
export function isGroupMonthRegistrationOpen(yearMonth, today = new Date()) {
	const [y, m] = yearMonth.split('-').map(Number);
	if (!y || !m) return false;
	const ref = new Date(today);
	ref.setHours(0, 0, 0, 0);

	const registrationOpenFor = (startDate) => {
		const cutoff = new Date(startDate);
		cutoff.setDate(cutoff.getDate() - GROUP_REGISTRATION_CUTOFF_DAYS);
		return cutoff > ref;
	};

	const mid = new Date(y, m - 1, 15);
	if (registrationOpenFor(mid)) return true;
	const last = new Date(y, m, 0);
	return registrationOpenFor(last);
}

/** YYYY-MM options for group departures: horizon ∩ seasonal months ∩ open registration. */
export function buildBookableMonthOptions(journey, horizonMonths = DEFAULT_HORIZON) {
	if (!journey) return [];
	const allowed = normalizeAvailableMonths(journey, 'group');
	if (!allowed.length) return [];
	const opts = [];
	const d = new Date();
	for (let i = 0; i < horizonMonths; i += 1) {
		const x = new Date(d.getFullYear(), d.getMonth() + i, 1);
		const month = x.getMonth() + 1;
		if (!allowed.includes(month)) continue;
		const value = `${x.getFullYear()}-${String(month).padStart(2, '0')}`;
		if (!isGroupMonthRegistrationOpen(value, d)) continue;
		opts.push({
			value,
			label: x.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
			shortLabel: x.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
		});
	}
	return opts;
}

function toIsoDate(d) {
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Min/max for private tour date input + allowed calendar months for validation. */
export function privateDateConstraints(journey, horizonMonths = DEFAULT_HORIZON) {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const max = new Date(today.getFullYear(), today.getMonth() + horizonMonths, 0);
	return {
		min: toIsoDate(today),
		max: toIsoDate(max),
		allowedMonths: normalizeAvailableMonths(journey, 'private'),
		seasonsLabel: seasonsLabel(journey, 'private'),
		horizonMonths,
	};
}

export function isPrivateDateAllowed(dateStr, journey, horizonMonths = DEFAULT_HORIZON) {
	if (!dateStr) return false;
	const { min, max, allowedMonths } = privateDateConstraints(journey, horizonMonths);
	if (dateStr < min || dateStr > max) return false;
	const month = Number(dateStr.split('-')[1]);
	return allowedMonths.includes(month);
}

export const MONTH_PRESETS = {
	all: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
	spring: [3, 4, 5],
	autumn: [10, 11],
};

export const MONTH_LABELS = MONTH_ABBR.map((label, i) => ({ value: i + 1, label }));
